import React from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRightOnRectangleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useAppDispatch } from '../../hooks/redux';
import { logoutUser } from '../../store/slices/authSlice';

import Button from './Button';

interface LogoutModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm?: () => void;
}

const LogoutModal: React.FC<LogoutModalProps> = ({ isOpen, onClose, onConfirm }) => {
  const dispatch = useAppDispatch();

  if (!isOpen) return null;

  const handleConfirmLogout = () => {
    onClose();
    if (onConfirm) {
      onConfirm();
    } else {
      dispatch(logoutUser());
    }
  };

  return createPortal(
    <AnimatePresence>
      <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 10 }}
          className="bg-white dark:bg-gray-900 rounded-3xl max-w-md w-full p-6 shadow-2xl border border-gray-100 dark:border-gray-800 text-center relative overflow-hidden"
        >
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>

          <div className="w-14 h-14 rounded-2xl bg-red-50 dark:bg-red-950/40 text-red-600 dark:text-red-400 flex items-center justify-center mx-auto mb-4 border border-red-100 dark:border-red-900/50 shadow-sm">
            <ArrowRightOnRectangleIcon className="w-7 h-7" />
          </div>

          <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
            Log out of ClickCollab?
          </h3>
          <div className="text-sm text-gray-600 dark:text-gray-400 mb-6 leading-relaxed space-y-1">
            <p>Are you sure you want to log out?</p>
            <p>You will need to sign in again to access your account.</p>
          </div>

          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={handleConfirmLogout}
              className="w-1/2 !text-primary-600 !border-primary-200 dark:!border-primary-900/50 hover:!bg-primary-50 dark:hover:!bg-primary-950/30 whitespace-nowrap text-sm font-medium py-2.5"
            >
              Log Out
            </Button>
            <Button
              onClick={onClose}
              className="w-1/2 !bg-primary-500 hover:!bg-primary-600 !text-white font-medium whitespace-nowrap text-sm py-2.5 shadow-md shadow-primary-500/20"
            >
              Stay
            </Button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>,
    document.body
  );
};

export default LogoutModal;
